import Icon from "../../common/components/Icon/Icon";
import { TrashIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { FloatingTooltip } from "../components/Tooltip/FloatingTooltip";
import ThreeDotsDropdownMenu from "../components/Icon/ThreeDotsDropdownMenu";
import {
  getProjectGroupMenuItems,
  getProjectMenuItems,
} from "../components/Icon/threeDotsMenuItems";
import type { IThreeDotClickPayload } from "./handleThreeDotsButtonClickForProjectsAndProjectGroups";
import type { IMedia } from "../../model/unityObject";
import type { IProjectGroupWithProjects } from "src/features/projectGroups/state/projectGroupsSlice";
import type { ProjectGroupListItem } from "src/pages/ProjectGroups/ModeListProjectAndProjectsGroup";

const isMediaItem = (item: ProjectGroupListItem): item is IMedia => "filename" in item;
const isProjectGroupItem = (item: ProjectGroupListItem): item is IProjectGroupWithProjects =>
  "auth_type" in item;

interface IDisplayActions {
  itemsForList: ProjectGroupListItem;
  openMediaDetail?: (id: number, item: unknown, index: number) => void;
  onDeleteMedia?: (media: IMedia) => void;
  isModalEdit?: boolean;
  isMediaList?: boolean;
  setEditedProjectGroupId?: (id: number, projectGroup: IProjectGroupWithProjects) => void;
  setEditedProjectId?: (id: number) => void;
  handleThreeDotButtonClick?: (payload: IThreeDotClickPayload) => void;
}

const DisplayActions = ({
  itemsForList,
  openMediaDetail = () => [],
  onDeleteMedia = () => [],
  isModalEdit,
  isMediaList,
  setEditedProjectGroupId = () => [],
  setEditedProjectId = () => [],
  handleThreeDotButtonClick = () => [],
}: IDisplayActions) => {
  const { t } = useTranslation();
  const threeDotsMenu = isProjectGroupItem(itemsForList) ? (
    <ThreeDotsDropdownMenu
      type="projectGroup"
      portal
      menuItems={getProjectGroupMenuItems(t, itemsForList.has_existing_build)}
      currentProjectGroupId={itemsForList.id}
      currentProjectGroup={itemsForList}
      setEditedProjectGroupId={setEditedProjectGroupId}
      handleClick={(e) =>
        handleThreeDotButtonClick({
          item: "projectGroup",
          itemId: itemsForList.id,
          action: e.itemId ?? "",
        })
      }
    />
  ) : (
    <ThreeDotsDropdownMenu
      type="project"
      portal
      menuItems={getProjectMenuItems(t)}
      projectId={itemsForList.id}
      setEditedProjectId={setEditedProjectId}
      handleClick={(e) =>
        handleThreeDotButtonClick({
          item: "project",
          itemId: itemsForList.id,
          action: e.itemId ?? "",
        })
      }
    />
  );

  const renderActionButtons = () => {
    if (isMediaList) {
      if (!isMediaItem(itemsForList)) {
        return <></>;
      }
      if (itemsForList.organization?.id !== 3) {
        return (
          <div className="flex">
            <FloatingTooltip content={t("tooltip.editMedia")} ariaHidden>
              <button
                className="mr-2"
                aria-label={t("tooltip.editMedia")}
                onClick={(e) => {
                  e.stopPropagation();
                  openMediaDetail(itemsForList.id, 0, 0);
                }}
              >
                <Icon icon={"PencilAltIcon"} className={" w-6 h-6 stroke-green-500"} />
              </button>
            </FloatingTooltip>
            {isModalEdit && (
              <FloatingTooltip content={t("tooltip.deleteMedia")} ariaHidden>
                <button
                  aria-label={t("tooltip.deleteMedia")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(itemsForList);
                  }}
                >
                  <TrashIcon className="w-6 h-6 stroke-red-500 mr-3" />
                </button>
              </FloatingTooltip>
            )}
          </div>
        );
      } else {
        return <></>;
      }
    } else {
      return (
        <div
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
          }}
        >
          {threeDotsMenu}
        </div>
      );
    }
  };

  return <div className="flex">{renderActionButtons()}</div>;
};

export default DisplayActions;
